import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { COGNITO_CONFIG } from "../../cognitoConfig";
import { RootState } from "../Store";
import { clearCookies, getAccessToken } from "services/cookieService";
import usersService from "services/userService";
import { generateRandomPassword } from "utils/passwordGenerator";
import { addToCart, clearCart } from "./cartSlice";
import { toast } from "react-hot-toast";
import { openEnquiryDialogue } from "./enquiryFormSlice";
import { userPool } from "cognitoAuthConfig.ts/userPool";
import { clearLogoState } from "./logosSlice";
import { clearMockupState } from "./mockupSlice";
import { clearCatalogState } from "./catalogSlice";
import { clearUser } from "./userSlice";
import { IUser } from "Interfaces/interface.user";
import { getCognitoErrorMessage } from "utils/ErrorHandlers";
import { IAuthState } from "Interfaces/interface.auth";

const initialState: IAuthState = {
  user: null,
  isAuthenticated: !!getAccessToken(),
  loading: false,
  error: null,
  tokens: {
    NGAT: getAccessToken(),
    // idToken: localStorage.getItem("idToken") || "",
    // refreshToken: localStorage.getItem("refreshToken") || "",
  },
  isAuthLoaded: false,
  forgotPassword: {
    loading: false,
    success: false,
  },
};

export const checkUserExists = createAsyncThunk<
  { exists: boolean },
  { email: string },
  { rejectValue: string }
>("auth/checkUserExists", async ({ email }, { rejectWithValue }) => {
  return new Promise((resolve, reject) => {
    if (!email) {
      const message = "Invalid email provided";
      toast.error(message);
      return reject(rejectWithValue(message));
    }

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: generateRandomPassword(), // any random password
    });

    user.authenticateUser(authDetails, {
      onSuccess: () => {
        // User exists and password was correct (unlikely with fake pwd)
        resolve({ exists: true });
      },
      onFailure: (err) => {
        if (err.code === "UserNotFoundException") {
          resolve({ exists: false });
        } else if (err.code === "NotAuthorizedException") {
          // Wrong password, but user exists
          resolve({ exists: true });
        } else {
          console.error("Cognito error in checkUserExists:", err);
          const message = err.message || "Something went wrong";
          toast.error(message);
          reject(rejectWithValue(message));
        }
      },
    });
  });
});

export const loginUser = createAsyncThunk<
  {
    user: IUser;
    tokens: { NGAT: string; idToken: string; refreshToken: string };
  },
  { username: string; password: string },
  { rejectValue: string }
>(
  "auth/signInUser",
  async ({ username, password }, { rejectWithValue, dispatch }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });

    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async (session) => {
          const NGAT = session.getAccessToken().getJwtToken();
          const idToken = session.getIdToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          cognitoUser.getUserAttributes(async (err, attributes) => {
            if (err || !attributes) {
              const message =
                getCognitoErrorMessage(err) ||
                "Failed to fetch user attributes";
              toast.error(message);
              return reject(rejectWithValue(message));
            }

            const attrMap: Record<string, string> = {};
            attributes.forEach((attr) => {
              attrMap[attr.getName()] = attr.getValue();
            });

            const user: IUser = {
              id: attrMap["sub"],
              emailAddress: attrMap["email"],
              phoneNumber: attrMap["phone_number"]?.replace(/^\+91/, ""),
              name: attrMap["name"],
              company: attrMap["custom:companyName"],
              emailVerified: attrMap["email_verified"] === "true",
              phoneNumberVerified: attrMap["phone_number_verified"] === "true",
            };

            // setAuthCookies(NGAT, idToken, refreshToken, user.id!);

            try {
              await usersService.getUserById(user.id!);
            } catch (err: any) {
              console.error("Failed to fetch user by ID:", err);
              toast.error(err.message || "Failed to load user details");
            }

            // Sync local cart items to backend
            const localCartItems = JSON.parse(
              localStorage.getItem("cart") || "[]"
            );
            if (localCartItems.length > 0) {
              localCartItems.forEach((item: any) => {
                dispatch<any>(
                  addToCart({
                    id: item.id,
                    productId: item.productId,
                    variantId: item.variantId || "",
                    quantity: item.quantity,
                    price: item.price || 0,
                    name: item.name || "Unknown Product",
                    images: item.images,
                  })
                );
              });
            }

            // Check for enquiry intent and reopen dialogue
            const enquiryIntent = JSON.parse(
              localStorage.getItem("enquiryIntent") || "{}"
            );
            if (enquiryIntent.products && enquiryIntent.quantities) {
              dispatch(
                openEnquiryDialogue({
                  products: enquiryIntent.products,
                  quantities: enquiryIntent.quantities,
                })
              );
              // Navigate to fromPath if available
              const navigate = (window as any).navigate; // Workaround for useNavigate in thunk
              if (navigate && enquiryIntent.fromPath) {
                navigate(enquiryIntent.fromPath);
              }
            }

            resolve({
              user,
              tokens: { NGAT, idToken, refreshToken },
            });
          });
        },
        onFailure: (err) => {
          console.log("err: ", err);
          const message = getCognitoErrorMessage(err);
          toast.error(message);
          reject(rejectWithValue(message));
        },
      });
    });
  }
);

export const registerGuestUser = createAsyncThunk<
  { email: string; tempPassword: string },
  { email: string; phoneNumber: string; name: string; companyName: string },
  { rejectValue: string }
>(
  "auth/registerGuestUser",
  async ({ email, phoneNumber, name, companyName }, { rejectWithValue }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
    const tempPassword = generateRandomPassword();
    const attributes = [
      new CognitoUserAttribute({ Name: "email", Value: email }),
      new CognitoUserAttribute({
        Name: "phone_number",
        Value: `+91${phoneNumber}`,
      }),
      new CognitoUserAttribute({ Name: "name", Value: name || "Guest User" }),
      new CognitoUserAttribute({
        Name: "custom:companyName",
        Value: companyName || "",
      }),
    ];

    return new Promise((resolve, reject) => {
      userPool.signUp(
        email,
        tempPassword,
        attributes,
        [],
        (err: any, result: any) => {
          if (err) {
            const message = getCognitoErrorMessage(err);
            toast.error(message);
            return reject(rejectWithValue(message));
          }
          if (result && result.user) {
            resolve({ email, tempPassword });
          } else {
            const message = "User creation failed";
            toast.error(message);
            reject(rejectWithValue(message));
          }
        }
      );
    });
  }
);

export const verifyOtp = createAsyncThunk<
  { verifiedType: "email" | "phone" },
  { type: "email" | "phone"; value: string; code: string },
  { rejectValue: string }
>("auth/verifyOtp", async ({ type, value, code }, { rejectWithValue }) => {
  const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
  });

  const cognitoUser = userPool.getCurrentUser();

  if (!cognitoUser) {
    const message = "No authenticated user found.";
    toast.error(message);
    return rejectWithValue(message);
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session?.isValid()) {
        const message = "Session invalid or expired. Please log in again.";
        toast.error(message);
        return reject(rejectWithValue(message));
      }

      if (type === "phone") {
        cognitoUser.verifyAttribute("phone_number", code, {
          onSuccess: () => {
            toast.success("Phone verified successfully");
            resolve({ verifiedType: "phone" });
          },
          onFailure: (err) => {
            const message = getCognitoErrorMessage(err);
            toast.error(message);
            reject(rejectWithValue(message));
          },
        });
      } else if (type === "email") {
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            const message = getCognitoErrorMessage(err);
            toast.error(message);
            return reject(rejectWithValue(message));
          }
          toast.success("Email verified successfully");
          resolve({ verifiedType: "email" });
        });
      }
    });
  });
});

export const verifyGuestOtp = createAsyncThunk<
  { verifiedType: "email" | "phone" },
  { type: "email" | "phone"; value: string; code: string },
  { rejectValue: string }
>("auth/verifyGuestOtp", async ({ type, value, code }, { rejectWithValue }) => {
  const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
  });

  const cognitoUser = new CognitoUser({
    Username: type === "phone" ? value.replace(/^\+91/, "") : value,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        const message = getCognitoErrorMessage(err);
        toast.error(message);
        return reject(rejectWithValue(message));
      }

      toast.success(
        `${type === "email" ? "Email" : "Phone"} verified successfully`
      );
      resolve({ verifiedType: type });
    });
  });
});

export const resendVerificationCode = createAsyncThunk<
  { type: "email" | "phone" },
  { type: "email" | "phone"; value: string },
  { rejectValue: string }
>(
  "auth/resendVerificationCode",
  async ({ type, value }, { rejectWithValue }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });

    // 👇 Always use the currently authenticated user
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      const message = "You must be logged in to verify your phone number.";
      toast.error(message);
      return rejectWithValue(message);
    }

    return new Promise((resolve, reject) => {
      // 👇 Ensure session is loaded
      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session?.isValid()) {
          const message = "Session expired or invalid. Please sign in again.";
          toast.error(message);
          return reject(rejectWithValue(message));
        }

        if (type === "email") {
          cognitoUser.resendConfirmationCode((err, result) => {
            if (err) {
              const message = getCognitoErrorMessage(err);
              toast.error(message);
              return reject(rejectWithValue(message));
            }
            resolve({ type });
          });
        } else if (type === "phone") {
          cognitoUser.getAttributeVerificationCode("phone_number", {
            onSuccess: () => resolve({ type }),
            onFailure: (err) => {
              const message = getCognitoErrorMessage(err);
              toast.error(message);
              reject(rejectWithValue(message));
            },
          });
        }
      });
    });
  }
);

export const loginGuestUser = createAsyncThunk<
  {
    user: IUser;
    tokens: { NGAT: string; idToken: string; refreshToken: string };
  },
  { email: string; password: string },
  { rejectValue: string }
>(
  "auth/loginGuestUser",
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });

    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async (session) => {
          const NGAT = session.getAccessToken().getJwtToken();
          const idToken = session.getIdToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();

          cognitoUser.getUserAttributes(async (err, attributes) => {
            if (err || !attributes) {
              const message =
                getCognitoErrorMessage(err) ||
                "Failed to fetch user attributes";
              toast.error(message);
              return reject(rejectWithValue(message));
            }

            const attrMap: Record<string, string> = {};
            attributes.forEach((attr) => {
              attrMap[attr.getName()] = attr.getValue();
            });

            const user: IUser = {
              id: attrMap["sub"],
              emailAddress: attrMap["email"],
              phoneNumber: attrMap["phone_number"]?.replace(/^\+91/, ""),
              name: attrMap["name"],
              company: attrMap["custom:companyName"],
              emailVerified: attrMap["email_verified"] === "true",
              phoneNumberVerified: attrMap["phone_number_verified"] === "true",
            };

            // setAuthCookies(NGAT, idToken, refreshToken, user.id!);

            try {
              await usersService.getUserById(user.id!);
            } catch (err: any) {
              console.error("Failed to fetch user by ID:", err);
              toast.error(err.message || "Failed to load user details");
            }

            // Check for enquiry intent and reopen dialogue
            const enquiryIntent = JSON.parse(
              localStorage.getItem("enquiryIntent") || "{}"
            );
            if (enquiryIntent.products && enquiryIntent.quantities) {
              dispatch(
                openEnquiryDialogue({
                  products: enquiryIntent.products,
                  quantities: enquiryIntent.quantities,
                })
              );
              // Navigate to fromPath if available
              const navigate = (window as any).navigate;
              if (navigate && enquiryIntent.fromPath) {
                navigate(enquiryIntent.fromPath);
              }
            }

            resolve({
              user,
              tokens: { NGAT, idToken, refreshToken },
            });
          });
        },
        onFailure: (err) => {
          const message = getCognitoErrorMessage(err);
          toast.error(message);
          reject(rejectWithValue(message));
        },
      });
    });
  }
);

export const verifyUpdatedEmail = createAsyncThunk<
  { verifiedEmail: string },
  { code: string },
  { rejectValue: string }
>(
  "auth/verifyUpdatedEmail",
  async ({ code }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const currentUser = state.auth.user;

    if (!currentUser) {
      const message = "No authenticated user found";
      toast.error(message);
      return rejectWithValue(message);
    }

    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      const message = "No authenticated user found";
      toast.error(message);
      return rejectWithValue(message);
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: any) => {
        if (err) {
          const message = getCognitoErrorMessage(err);
          toast.error(message);
          return reject(rejectWithValue(message));
        }

        cognitoUser.verifyAttribute("email", code, {
          onSuccess: () => {
            resolve({ verifiedEmail: currentUser.emailAddress! });
          },
          onFailure: (err: any) => {
            const message = getCognitoErrorMessage(err);
            toast.error(message);
            reject(rejectWithValue(message));
          },
        });
      });
    });
  }
);

export const changePassword = createAsyncThunk<
  { success: boolean },
  { currentPassword: string; newPassword: string },
  { rejectValue: string }
>(
  "auth/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue, getState }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      const message = "No authenticated user found";
      toast.error(message);
      return rejectWithValue(message);
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session.isValid()) {
          const message =
            getCognitoErrorMessage(err) ||
            "Invalid session. Please sign in again.";
          toast.error(message);
          return reject(rejectWithValue(message));
        }

        cognitoUser.changePassword(
          currentPassword,
          newPassword,
          (err, result) => {
            if (err) {
              const message = getCognitoErrorMessage(err);
              toast.error(message);
              return reject(rejectWithValue(message));
            }
            resolve({ success: true });
          }
        );
      });
    });
  }
);

export const signupUser = createAsyncThunk<
  { user: IUser },
  {
    email: string;
    password: string;
    name: string;
    companyName: string;
    // phoneNumber?: string;
  },
  { rejectValue: string }
>("auth/signupUser", async (credentials, { rejectWithValue }) => {
  const { email, password, name, companyName } = credentials;
  const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
  });

  const attributeList = [
    new CognitoUserAttribute({ Name: "email", Value: email }),
    new CognitoUserAttribute({
      Name: "name",
      Value: name,
    }),
    new CognitoUserAttribute({
      Name: "custom:companyName",
      Value: companyName,
    }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) {
        console.log("err: ", err);
        const message = getCognitoErrorMessage(err);
        toast.error(message);
        return reject(rejectWithValue(message));
      }
      if (result && result.user) {
        const user: IUser = {
          emailAddress: email,
          name,
          company: companyName,
          // phoneNumber,
        };
        resolve({ user });
      } else {
        const message = "User creation failed";
        toast.error(message);
        reject(rejectWithValue(message));
      }
    });
  });
});

export const handleCognitoRedirect = createAsyncThunk<
  {
    user: IUser;
    tokens: {
      NGAT: string;
      idToken: string;
      refreshToken: string;
    };
    redirectPath: string;
  },
  void,
  { rejectValue: string }
>("auth/handleCognitoRedirect", async (_, { rejectWithValue }) => {
  const query = new URLSearchParams(window.location.search);
  const authCode = query.get("code");
  const redirectPath = query.get("state") || "/";

  if (!authCode) return rejectWithValue("No authorization code found");

  const { ClientId: clientId, UserPoolId } = COGNITO_CONFIG;
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;

  const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI!;

  const tokenUrl = `https://${domain}/oauth2/token`;

  try {
    const response = await fetch(tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code: authCode,
        redirect_uri: redirectUri,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return rejectWithValue(
        data.error_description || "Failed to exchange authorization code"
      );
    }

    const { access_token, id_token, refresh_token } = data;

    if (!access_token || !id_token) {
      return rejectWithValue("Invalid tokens received from Cognito");
    }

    let payload;
    try {
      payload = JSON.parse(atob(id_token.split(".")[1]));
    } catch {
      return rejectWithValue("Invalid ID token format");
    }

    const userId = payload.sub;
    const emailAddress = payload.email;

    if (!userId || !emailAddress) {
      return rejectWithValue("Missing user ID or email in token");
    }

    const userPool = new CognitoUserPool({ UserPoolId, ClientId: clientId });

    const cognitoUser = new CognitoUser({
      Username: emailAddress,
      Pool: userPool,
    });

    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: id_token }),
      AccessToken: new CognitoAccessToken({ AccessToken: access_token }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: refresh_token }),
    });

    cognitoUser.setSignInUserSession(session);

    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((err, attributes) => {
        console.log("err: ", err);
        if (err || !attributes) {
          return reject(
            rejectWithValue("Failed to fetch user attributes from Cognito")
          );
        }

        const attrMap: Record<string, string> = {};
        attributes.forEach((attr) => {
          attrMap[attr.getName()] = attr.getValue();
        });

        const user: IUser = {
          id: attrMap["sub"],
          emailAddress: attrMap["email"],
          phoneNumber: attrMap["phone_number"]?.replace(/^\+91/, ""),
          name: attrMap["name"] || attrMap["name"]?.split(" ")[0],
          company: attrMap["custom:companyName"],
          emailVerified: attrMap["email_verified"] === "true",
          phoneNumberVerified: attrMap["phone_number_verified"] === "true",
        };

        // setAuthCookies(access_token, id_token, refresh_token, userId);

        resolve({
          user,
          tokens: {
            NGAT: access_token,
            idToken: id_token,
            refreshToken: refresh_token,
          },
          redirectPath,
        });
      });
    });
  } catch (error: any) {
    console.log("error: ", error);
    return rejectWithValue(
      error.message || "Unknown error during token exchange"
    );
  }
});

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, thunkAPI) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
      toast.success("Signed out successfully!");
    }
    clearCookies();
    thunkAPI.dispatch(clearUser());
    thunkAPI.dispatch(clearCart());
    thunkAPI.dispatch(clearLogoState());
    thunkAPI.dispatch(clearMockupState());
    thunkAPI.dispatch(clearCatalogState());
  }
);

export const updateUser = createAsyncThunk<
  { user: IUser },
  {
    name: string;
    emailAddress?: string;
    phoneNumber?: string;
    company?: string;
  },
  { rejectValue: string }
>("auth/updateUser", async (credentials, { rejectWithValue }) => {
  const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
  });

  const cognitoUser = userPool.getCurrentUser();

  if (!cognitoUser) {
    const message = "No authenticated user found";
    toast.error(message);
    return rejectWithValue(message);
  }

  const getSessionAsync = () =>
    new Promise<any>((resolve, reject) => {
      cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          reject(err);
        } else {
          resolve(session);
        }
      });
    });

  const updateAttributesAsync = (attrs: CognitoUserAttribute[]) =>
    new Promise<string>((resolve, reject) => {
      cognitoUser.updateAttributes(attrs, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result ?? "SUCCESS");
        }
      });
    });

  try {
    await getSessionAsync();

    const attributeList: CognitoUserAttribute[] = [];

    if (credentials.name) {
      attributeList.push(
        new CognitoUserAttribute({
          Name: "name",
          Value: credentials.name.trim(),
        })
      );
    }
    if (credentials.emailAddress) {
      attributeList.push(
        new CognitoUserAttribute({
          Name: "email",
          Value: credentials.emailAddress,
        })
      );
    }
    if (credentials.phoneNumber) {
      attributeList.push(
        new CognitoUserAttribute({
          Name: "phone_number",
          Value: `+91${credentials.phoneNumber}`,
        })
      );
    }
    if (credentials.company) {
      attributeList.push(
        new CognitoUserAttribute({
          Name: "custom:companyName",
          Value: credentials.company,
        })
      );
    }

    if (attributeList.length > 0) {
      await updateAttributesAsync(attributeList);
    }

    const user: IUser = {
      id: cognitoUser.getUsername(),
      name: credentials.name,
      emailAddress: credentials.emailAddress || "",
      phoneNumber: credentials.phoneNumber,
      company: credentials.company,
    };

    try {
      await usersService.updateUser(user);
    } catch (error: any) {
      console.error("Backend update failed:", error.message);
      toast.error("Backend update failed");
    }

    return { user };
  } catch (err: any) {
    const message = getCognitoErrorMessage(err) || "Failed to update user";
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const forgotPassword = createAsyncThunk<
  { email: string },
  { email: string },
  { rejectValue: string }
>("auth/forgotPassword", async ({ email }, { rejectWithValue }) => {
  const userPool = new CognitoUserPool({
    UserPoolId: COGNITO_CONFIG.UserPoolId,
    ClientId: COGNITO_CONFIG.ClientId,
  });
  const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve({ email });
      },
      onFailure: (err) => {
        const message = getCognitoErrorMessage(err);
        toast.error(message);
        reject(rejectWithValue(message));
      },
    });
  });
});

export const confirmForgotPassword = createAsyncThunk<
  { email: string },
  { email: string; code: string; newPassword: string },
  { rejectValue: string }
>(
  "auth/confirmForgotPassword",
  async ({ email, code, newPassword }, { rejectWithValue }) => {
    const userPool = new CognitoUserPool({
      UserPoolId: COGNITO_CONFIG.UserPoolId,
      ClientId: COGNITO_CONFIG.ClientId,
    });
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve({ email });
        },
        onFailure: (err) => {
          const message = getCognitoErrorMessage(err);
          toast.error(message);
          reject(rejectWithValue(message));
        },
      });
    });
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<IUser | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.isAuthLoaded = true;
    },
    clearError: (state) => {
      // No-op since error state is removed
    },
    resetForgotPasswordState: (state) => {
      state.forgotPassword = {
        loading: false,
        success: false,
      };
    },
    clearAuthState: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerGuestUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerGuestUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthLoaded = true;
      })
      .addCase(registerGuestUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          if (action.payload.verifiedType === "email")
            state.user.emailVerified = true;
          if (action.payload.verifiedType === "phone")
            state.user.phoneNumberVerified = true;
        }
      })
      .addCase(verifyOtp.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginGuestUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginGuestUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokens = action.payload.tokens;
        state.isAuthLoaded = true;
      })
      .addCase(loginGuestUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokens = action.payload.tokens;
        state.isAuthLoaded = true;
      })
      .addCase(loginUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = false;
        state.isAuthLoaded = true;
      })
      .addCase(signupUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(handleCognitoRedirect.pending, (state) => {
        state.loading = true;
      })
      .addCase(handleCognitoRedirect.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokens = action.payload.tokens;
        state.isAuthLoaded = true;
      })
      .addCase(handleCognitoRedirect.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.tokens = {};
        state.isAuthLoaded = false;
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthLoaded = true;
      })
      .addCase(updateUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(verifyUpdatedEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyUpdatedEmail.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          state.user.emailVerified = true;
        }
      })
      .addCase(verifyUpdatedEmail.rejected, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.success = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.forgotPassword.loading = false;
      })
      .addCase(confirmForgotPassword.pending, (state) => {
        state.forgotPassword.loading = true;
        state.forgotPassword.success = false;
      })
      .addCase(confirmForgotPassword.fulfilled, (state) => {
        state.forgotPassword.loading = false;
        state.forgotPassword.success = true;
      })
      .addCase(confirmForgotPassword.rejected, (state) => {
        state.forgotPassword.loading = false;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state) => {
        state.loading = false;
      })
      .addCase(resendVerificationCode.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendVerificationCode.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendVerificationCode.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setUser, clearError, resetForgotPasswordState, clearAuthState } =
  authSlice.actions;
export default authSlice.reducer;

export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectIsAuthLoaded = (state: RootState) => state.auth.isAuthLoaded;
export const selectForgotPassword = (state: RootState) =>
  state.auth.forgotPassword;
